import { useMemo } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useRequiresAuthorization } from '@ibk/shared-permissioning';
import { FeatureFlagUseCase, PermissionsDomains, PermissionsResources, ProductAlias, } from '@ibk/shared-types';
import { normalizeProductResponse, productApi } from '../services/home/productApi';
import { useAppSelector } from '../store/hooks';
import { selectSelectedAccountId, selectSelectedCompanyOrgId } from '../store/selectors/userSelectors';
export const useProduct = (productAlias, { enabled = true } = {}) => {
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const selectedCompanyOrgId = useAppSelector(selectSelectedCompanyOrgId);
    const canSeeBalance = useRequiresAuthorization(PermissionsDomains.ACCOUNT, [
        PermissionsResources.ACCOUNT_VIEW_BALANCE,
    ]);
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const queryParams = useMemo(() => ({
        productAlias,
        selectedCompanyPartyId: selectedAccountId || '',
        selectedCompanyOrgId: selectedCompanyOrgId || '',
        accountPjCanSeeBalance: productAlias === ProductAlias.CONTA_DIGITAL ? canSeeBalance : false,
    }), [productAlias, canSeeBalance, selectedAccountId, selectedCompanyOrgId]);
    const skipQuery = useMemo(() => {
        if (!enabled || !selectedAccountId)
            return true;
        if (productAlias === ProductAlias.BENEFICIOS && !selectedCompanyOrgId) {
            return true;
        }
        return false;
    }, [enabled, selectedAccountId, productAlias, selectedCompanyOrgId]);
    const queryOptions = { refetchOnMountOrArgChange: true, refetchOnFocus: false, refetchOnReconnect: true };
    const queryV1 = productApi.useGetHomeProductQuery(queryParams, {
        ...queryOptions,
        skip: skipQuery || isV2Enabled,
    });
    const queryV2 = productApi.useGetHomeProductV2Query(queryParams, {
        ...queryOptions,
        skip: skipQuery || !isV2Enabled,
    });
    const query = isV2Enabled ? queryV2 : queryV1;
    return useMemo(() => {
        let normalizedData = null;
        let errorMessage = null;
        if (query.data && typeof query.data === 'object' && 'result' in query.data) {
            normalizedData = normalizeProductResponse(query.data.result);
        }
        if (query.error) {
            if (productAlias === ProductAlias.BENEFICIOS && !selectedCompanyOrgId) {
                errorMessage = 'Benefícios requires company organization ID';
            }
            else {
                errorMessage = `Falha ao carregar produto ${productAlias}`;
            }
        }
        return {
            data: normalizedData,
            isLoading: query.isLoading,
            isFetching: query.isFetching,
            refetch: query.refetch,
            error: errorMessage,
        };
    }, [query.data, query.isLoading, query.isFetching, query.refetch, query.error, productAlias, selectedCompanyOrgId]);
};
export const useProducts = (productAliases, { enabled = true } = {}) => {
    const contaDigital = useProduct(ProductAlias.CONTA_DIGITAL, {
        enabled: enabled && productAliases.includes(ProductAlias.CONTA_DIGITAL),
    });
    const cobrancas = useProduct(ProductAlias.COBRANCAS, {
        enabled: enabled && productAliases.includes(ProductAlias.COBRANCAS),
    });
    const contabilidadeDigital = useProduct(ProductAlias.CONTABILIDADE_DIGITAL, {
        enabled: enabled && productAliases.includes(ProductAlias.CONTABILIDADE_DIGITAL),
    });
    const beneficios = useProduct(ProductAlias.BENEFICIOS, {
        enabled: enabled && productAliases.includes(ProductAlias.BENEFICIOS),
    });
    const programaFidelidade = useProduct(ProductAlias.DOMINIO_RECOMPENSAS, {
        enabled: enabled && productAliases.includes(ProductAlias.DOMINIO_RECOMPENSAS),
    });
    return useMemo(() => {
        const products = {
            [ProductAlias.CONTA_DIGITAL]: contaDigital,
            [ProductAlias.COBRANCAS]: cobrancas,
            [ProductAlias.CONTABILIDADE_DIGITAL]: contabilidadeDigital,
            [ProductAlias.BENEFICIOS]: beneficios,
            [ProductAlias.DOMINIO_RECOMPENSAS]: programaFidelidade,
        };
        const isLoading = Object.values(products).some(result => result.isLoading);
        const hasError = Object.values(products).some(result => result.error !== null);
        return {
            products,
            isLoading,
            hasError,
        };
    }, [contaDigital, cobrancas, contabilidadeDigital, beneficios, programaFidelidade]);
};
